import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useSession } from '@/lib/auth-client';
import { signOut, authClient } from '@/lib/auth-client';
import type { Organization as BetterAuthOrganization, Member as BetterAuthMember } from 'better-auth/plugins/organization';
import { organizationService } from '@/services/organization-service';
import { apiKeyService, type ApiKey } from '@/services/api-key-service';
import { getLastActiveOrganizationId } from '@/services/user-metadata-service';

type Organization = BetterAuthOrganization;
type ActiveOrganization = BetterAuthMember & { organization: Organization };

interface UserProviderType {
  user: any | null;
  organizations: Organization[];
  activeOrganization: ActiveOrganization | null;
  apiKeys: ApiKey[];
  isAuthenticated: boolean;
  isLoading: boolean;
  isOrgLoading: boolean;
  isApiKeysLoading: boolean;
  error: string | null;
  logout: (redirectPath?: string) => Promise<void>;
  switchOrganization: (orgId: string) => Promise<void>;
  refreshOrganizations: () => Promise<void>;
  refreshApiKeys: () => Promise<void>;
  refreshRole: () => Promise<void>;
}

const UserContext = createContext<UserProviderType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
  onLogoutRedirect?: (path?: string) => void;
}

export const UserProvider: React.FC<UserProviderProps> = ({
  children,
  onLogoutRedirect
}) => {
  const { data: session, isPending: isAuthPending } = useSession();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [activeOrganization, setActiveOrganization] = useState<ActiveOrganization | null>(null);
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [isOrgLoading, setIsOrgLoading] = useState(true);
  const [isApiKeysLoading, setIsApiKeysLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshOrganizations = async () => {
    if (!session?.user) {
      setOrganizations([]);
      setActiveOrganization(null);
      return;
    }

    try {
      setIsOrgLoading(true);
      setError(null);

      // Use organization service
      const orgs = await organizationService.list();
      setOrganizations(orgs);

      // Fetch active organization
      const activeMember = await organizationService.getActiveMember();
      if (activeMember) {
        // Find the full organization details
        const orgId = activeMember.organizationId;
        const fullOrg = orgs.find(org => org.id === orgId);

        if (fullOrg) {
          const activeOrgWithDetails: ActiveOrganization = {
            ...activeMember,
            organization: fullOrg
          };
          setActiveOrganization(activeOrgWithDetails);
        } else {
          // Fallback if organization details not found
          setActiveOrganization({
            ...activeMember,
            organization: {
              id: orgId,
              name: '',
              slug: '',
              createdAt: new Date(),
              logo: null,
              metadata: undefined
            }
          });
        }
      } else if (orgs.length > 0) {
        // No active organization in current session, but user has organizations
        // Restore from: 1) localStorage (current session), 2) DB (user preference), 3) first org (default)
        console.warn('No active organization found. Restoring from localStorage and database.');

        let orgToActivate = orgs[0]; // Default to first org
        const sessionID = (session as any)?.id;

        // Priority 1: Try to restore from localStorage (current session cache)
        if (sessionID && typeof window !== 'undefined') {
          try {
            const sessionDataStr = localStorage.getItem(`session_${sessionID}`);
            if (sessionDataStr) {
              const sessionData = JSON.parse(sessionDataStr) as { activeOrgId?: string };
              const storedOrgID = sessionData.activeOrgId;

              // Verify the stored org still exists in user's org list
              if (storedOrgID) {
                const storedOrg = orgs.find(org => org.id === storedOrgID);
                if (storedOrg) {
                  orgToActivate = storedOrg;
                  console.log('Restored organization from localStorage for session:', sessionID);
                }
              }
            }
          } catch (err) {
            console.error('Error reading session data from localStorage:', err);
          }
        }

        // Priority 2: If not found in localStorage, try to fetch from database
        if (orgToActivate === orgs[0]) {
          // Still using default, try database
          try {
            const lastActiveOrgId = await getLastActiveOrganizationId();
            if (lastActiveOrgId) {
              const dbOrg = orgs.find(org => org.id === lastActiveOrgId);
              if (dbOrg) {
                orgToActivate = dbOrg;
                console.log('Restored organization from database:', lastActiveOrgId);
              }
            }
          } catch (err) {
            console.error('Error fetching last active organization from database:', err);
          }
        }

        try {
          // Set the organization as active in the session
          await organizationService.setActive(orgToActivate.id);

          // Fetch the active member after setting it
          let newActiveMember = await organizationService.getActiveMember();

          // If getActiveMember still returns null, it might be a timing issue
          // Create a member object from what we know
          if (!newActiveMember && orgToActivate) {
            console.warn('getActiveMember returned null after setActive, using fallback member data');
            newActiveMember = {
              id: `member_${orgToActivate.id}`,
              organizationId: orgToActivate.id,
              userId: session?.user?.id,
              role: 'member', // Default role, will be updated
              createdAt: new Date(),
            } as any;
          }

          if (newActiveMember) {
            const activeOrgWithDetails: ActiveOrganization = {
              ...newActiveMember,
              organization: orgToActivate
            };
            setActiveOrganization(activeOrgWithDetails);

            // Store this session's data in localStorage for quick access next time
            if (sessionID && typeof window !== 'undefined') {
              try {
                let sessionData: Record<string, any> = {};
                const existing = localStorage.getItem(`session_${sessionID}`);
                if (existing) {
                  sessionData = JSON.parse(existing);
                }
                sessionData.activeOrgId = orgToActivate.id;
                localStorage.setItem(`session_${sessionID}`, JSON.stringify(sessionData));
              } catch (err) {
                console.error('Error storing session data to localStorage:', err);
              }
            }
          } else {
            setActiveOrganization(null);
          }
        } catch (err: any) {
          console.error('Failed to activate organization:', err);
          setActiveOrganization(null);
        }
      } else {
        // User has no organizations
        setActiveOrganization(null);
      }
    } catch (err: any) {
      console.error('Error fetching organizations:', err);
      setError(err.message || 'Failed to fetch organization data');
      setOrganizations([]);
      setActiveOrganization(null);
    } finally {
      setIsOrgLoading(false);
    }
  };

  const refreshApiKeys = async () => {
    if (!session?.user) {
      setApiKeys([]);
      return;
    }

    try {
      setIsApiKeysLoading(true);
      setError(null);

      // Use API key service
      const keys = await apiKeyService.list();
      setApiKeys(keys);
    } catch (err: any) {
      console.error('Error fetching API keys:', err);
      setError(err.message || 'Failed to fetch API keys');
      setApiKeys([]);
    } finally {
      setIsApiKeysLoading(false);
    }
  };

  useEffect(() => {
    if (session?.user) {
      refreshOrganizations();
      refreshApiKeys();
    }
  }, [session?.user]);

  const switchOrganization = async (orgId: string) => {
    try {
      await organizationService.setActive(orgId);
      // Refresh the organization data after switching
      await refreshOrganizations();
    } catch (err: any) {
      console.error('Error switching organization:', err);
      setError(err.message || 'Failed to switch organization');
      throw err;
    }
  };

  const refreshRole = async () => {
    if (!session?.user) {
      setActiveOrganization(null);
      return;
    }

    try {
      setError(null);
      // Fetch the current active member role
      const activeMember = await organizationService.getActiveMember();
      if (activeMember) {
        // Find the full organization details
        const orgs = organizations.length > 0 ? organizations : await organizationService.list();
        const orgId = activeMember.organizationId;
        const fullOrg = orgs.find(org => org.id === orgId);

        if (fullOrg) {
          const activeOrgWithDetails: ActiveOrganization = {
            ...activeMember,
            organization: fullOrg
          };
          setActiveOrganization(activeOrgWithDetails);
        } else {
          // Update only the role and member info
          setActiveOrganization({
            ...activeMember,
            organization: {
              id: orgId,
              name: '',
              slug: '',
              createdAt: new Date(),
              logo: null,
              metadata: undefined
            }
          });
        }
      } else {
        console.warn('Failed to fetch current role for active organization');
        setActiveOrganization(null);
      }
    } catch (err: any) {
      console.error('Error refreshing role:', err);
      setError(err.message || 'Failed to refresh role');
    }
  };

  const logout = async (redirectPath?: string) => {
    try {
      // Call Better Auth signOut with onSuccess callback for redirect after successful logout
      await authClient.signOut({
        fetchOptions: {
          onSuccess: () => {
            // Use callback if provided, otherwise default to login page
            if (onLogoutRedirect) {
              onLogoutRedirect(redirectPath);
            } else {
              window.location.href = redirectPath || '/login';
            }
          },
        },
      });
    } catch (error) {
      console.error('Error during logout:', error);
      // Fallback to manual redirect if signOut fails
      if (onLogoutRedirect) {
        onLogoutRedirect(redirectPath);
      } else {
        window.location.href = redirectPath || '/login';
      }
    }
  };

  const value: UserProviderType = {
    user: session?.user || null,
    organizations,
    activeOrganization,
    apiKeys,
    isAuthenticated: !!session?.user,
    isLoading: isAuthPending,
    isOrgLoading,
    isApiKeysLoading,
    error,
    logout,
    switchOrganization,
    refreshOrganizations,
    refreshApiKeys,
    refreshRole
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
